import React, { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAdminGuard } from '../hooks/useAdminGuard';
import AdminLayout from '../components/AdminLayout';
import BookingsTable from '../components/BookingsTable';
import EditBookingDrawer from '../components/EditBookingDrawer';
import AvailabilityEditor from '../components/AvailabilityEditor';
import SettingsPanel from '../components/SettingsPanel';
import AdminCalendar from '../components/AdminCalendar';
import LeagueAdminPanel from '../components/LeagueAdminPanel';
import AdminMfaGate from '../components/AdminMfaGate';
import { LEAGUE_COMING_SOON } from '../constants/league';

const Admin = () => {
  const { isAdmin, checking, mfaEnrollmentRequired, needsMfaSignIn, logout } = useAdminGuard();
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  // Show loading state while checking admin status
  if (checking) {
    return (
      <div style={{ backgroundColor: '#090909', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(2rem, 6vw, 5rem)' }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '3rem',
              height: '3rem',
              borderRadius: '999px',
              border: '2px solid #374151',
              borderTop: '2px solid #C9A84C',
              margin: '0 auto 1rem',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: '#ffffff' }}>Verifying admin access...</p>
        </div>
        <style>{`@keyframes spin { from {transform: rotate(0deg);} to {transform: rotate(360deg);} }`}</style>
      </div>
    );
  }

  // MFA gate — admin role present but session not MFA-authenticated
  if (mfaEnrollmentRequired || needsMfaSignIn) {
    return (
      <AdminMfaGate
        mfaEnrollmentRequired={mfaEnrollmentRequired}
        needsMfaSignIn={needsMfaSignIn}
        onSignOut={logout}
      />
    );
  }

  // If not admin, the hook will redirect to /admin/login
  if (!isAdmin) {
    return null;
  }

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setIsEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setSelectedBooking(null);
  };

  const handleSaveBooking = (updatedBooking) => {
    // Here you would typically refresh the bookings list
    console.log('Booking updated:', updatedBooking);
    setIsEditDrawerOpen(false);
    setSelectedBooking(null);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'bookings':
        return <BookingsTable onEditBooking={handleEditBooking} />;
      case 'calendar':
        return <AdminCalendar />;
      case 'availability':
        return <AvailabilityEditor />;
      case 'league':
        return LEAGUE_COMING_SOON ? null : <LeagueAdminPanel />;
      case 'settings':
        return <SettingsPanel />;
      default:
        return <BookingsTable onEditBooking={handleEditBooking} />;
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151',
          },
          success: {
            style: {
              background: '#065f46',
              color: '#fff',
              border: '1px solid #10b981',
            },
          },
          error: {
            style: {
              background: '#991b1b',
              color: '#fff',
              border: '1px solid #ef4444',
            },
          },
        }}
      />
      
      <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab}>
        {renderTabContent()}
      </AdminLayout>

      <EditBookingDrawer
        isOpen={isEditDrawerOpen}
        onClose={handleCloseEditDrawer}
        booking={selectedBooking}
        onSave={handleSaveBooking}
      />
    </>
  );
};

export default Admin;